import { useEffect, useState } from "react";
import type { Game } from "@engine/core/Game";
import type { EnabledMap, PerfStats } from "@engine/debug";

interface Props {
  sceneName?: string | undefined;
  game?: Game | undefined;
}

const SAMPLE_INTERVAL_MS = 250;

const HOTKEYS: Array<{ key: "F1" | "F2" | "F3" | "F4"; cat: keyof EnabledMap }> = [
  { key: "F1", cat: "stats" },
  { key: "F2", cat: "collision" },
  { key: "F3", cat: "chunks" },
  { key: "F4", cat: "net" },
];

const DEFAULT_ENABLED: EnabledMap = { stats: false, collision: false, chunks: false, net: false };
const DEFAULT_STATS: PerfStats = { fps: 0, msFrame: 0, entities: 0, displayObjects: 0 };

export function DebugPanel({ sceneName, game }: Props) {
  const [stats, setStats] = useState<PerfStats>(DEFAULT_STATS);
  const [enabled, setEnabled] = useState<EnabledMap>(DEFAULT_ENABLED);
  const [activeLines, setActiveLines] = useState<string[]>([]);

  useEffect(() => {
    if (!game) return;
    setStats(game.perf.getStats());
    setEnabled(game.overlay.enabled());
    setActiveLines(game.overlay.getActiveLines());
    const id = window.setInterval(() => {
      setStats(game.perf.getStats());
      setActiveLines(game.overlay.getActiveLines());
    }, SAMPLE_INTERVAL_MS);
    const unsub = game.overlay.subscribe((next) => {
      setEnabled(next);
      setActiveLines(game.overlay.getActiveLines());
    });
    return () => {
      window.clearInterval(id);
      unsub();
    };
  }, [game]);

  return (
    <div
      data-testid="debug-panel"
      style={{
        position: "fixed",
        top: 8,
        left: 8,
        padding: "6px 10px",
        background: "rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.4,
        pointerEvents: "none",
        zIndex: 1000,
        fontFamily: "ui-monospace, monospace",
        color: "#e6e8ee",
        minWidth: 200,
      }}
    >
      <div data-testid="debug-fps">FPS: {stats.fps}</div>
      <div style={{ opacity: 0.85 }}>
        ms/frame: {stats.msFrame.toFixed(2)} · entities: {stats.entities} · objs: {stats.displayObjects}
      </div>
      <div style={{ opacity: 0.85 }}>scene: {sceneName ?? "—"}</div>
      <div style={{ opacity: 0.6, marginTop: 2 }}>WASD/arrows · space=shake · wheel=zoom</div>
      <div style={{ opacity: 0.6 }}>
        {HOTKEYS.map(({ key, cat }) => (
          <span
            key={key}
            style={{
              marginRight: 6,
              padding: "0 4px",
              borderRadius: 3,
              background: enabled[cat] ? "#2563eb" : "transparent",
              color: enabled[cat] ? "#fff" : "rgba(230,232,238,0.55)",
            }}
            title={`${key} = ${cat}`}
          >
            {key}·{cat}
          </span>
        ))}
      </div>
      {activeLines.length > 0 && (
        <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
          {activeLines.map((line, i) => (
            <div
              key={`${line}-${i}`}
              style={{
                opacity: line.startsWith("[") ? 0.95 : 0.78,
                fontWeight: line.startsWith("[") ? 600 : 400,
              }}
            >
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
